import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  IconButton,
  Chip,
  Stack,
  Divider
} from '@mui/material';
import {
  Close as CloseIcon,
  AutoAwesome as AutoAwesomeIcon,
  Build as BuildIcon,
  SmartToy as SmartToyIcon,
  Psychology as PsychologyIcon,
  Speed as SpeedIcon,
  Security as SecurityIcon,
  CloudDownload as CloudDownloadIcon,
  BusinessCenter as BusinessCenterIcon,
  Code as CodeIcon,
  Api as ApiIcon,
  Memory as MemoryIcon,
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';

const PromotionModal = ({ open, onClose, onContactClick }) => {
  const handleContactClick = () => {
    onContactClick();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      sx={{ zIndex: 1200 }}
      PaperProps={{
        sx: {
          borderRadius: 4,
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(247, 250, 252, 0.98) 100%)',
          backdropFilter: 'blur(20px)',
          boxShadow: '0 20px 60px rgba(26, 54, 93, 0.3)',
          border: '2px solid rgba(26, 54, 93, 0.1)',
          position: 'relative',
          overflow: 'hidden',
          zIndex: 1200,
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 6,
            background: 'linear-gradient(135deg, #1A365D 0%, #D69E2E 50%, #2D3748 100%)',
            zIndex: 1
          }
        }
      }}
    >
      {/* 헤더 */}
      <DialogTitle sx={{ 
        pb: 1,
        background: 'linear-gradient(135deg, #1A365D 0%, #2D3748 100%)',
        color: 'white',
        position: 'relative',
        zIndex: 2,
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: 1,
          background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
        }
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ 
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 48,
              height: 48,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #FBD38D 0%, #D69E2E 100%)',
              color: '#1A365D',
              boxShadow: '0 8px 24px rgba(251, 211, 141, 0.4)'
            }}>
              <BuildIcon sx={{ fontSize: 28 }} />
            </Box>
            <Box>
              <Typography variant="h4" sx={{ 
                fontWeight: 800,
                fontSize: { xs: '1.2rem', md: '1.8rem' },
                background: 'linear-gradient(135deg, #FBD38D 0%, #D69E2E 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 0.5
              }}>
                🚀 맞춤형 AI 솔루션 개발 서비스
              </Typography>
              <Typography variant="body1" sx={{ 
                color: 'rgba(255, 255, 255, 0.9)',
                fontSize: { xs: '0.8rem', md: '1rem' },
                fontWeight: 500
              }}>
                RAG 시스템 외에도 다양한 AI/LLM 기반 솔루션을 제공합니다
              </Typography>
            </Box>
          </Box>
          <IconButton 
            onClick={onClose}
            sx={{ 
              color: 'white',
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
              zIndex: 1300,
              position: 'relative',
              minWidth: 40,
              minHeight: 40,
              '&:hover': { 
                background: 'rgba(255, 255, 255, 0.2)',
                transform: 'scale(1.1)'
              },
              '&:active': {
                transform: 'scale(0.95)'
              },
              transition: 'all 0.2s ease-in-out'
            }}
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: { xs: 1.5, md: 4 }, maxHeight: { xs: '70vh', md: '80vh' }, overflow: 'auto' }}>
        {/* 메인 메시지 */}
        <Box sx={{ 
          textAlign: 'center', 
          mb: { xs: 2, md: 4 },
          p: { xs: 2, md: 3 },
          background: 'linear-gradient(135deg, rgba(26, 54, 93, 0.05) 0%, rgba(214, 158, 46, 0.05) 100%)',
          borderRadius: 3,
          border: '2px solid rgba(26, 54, 93, 0.1)'
        }}>
          <Typography variant="h5" sx={{ 
            fontWeight: 700,
            color: '#1A365D',
            mb: 2,
            fontSize: { xs: '1.1rem', md: '1.4rem' }
          }}>
            💡 "RAG 시스템이 필요하지 않으신가요?"
          </Typography>
          <Typography variant="body1" sx={{ 
            color: '#4A5568',
            fontSize: { xs: '0.9rem', md: '1.1rem' },
            lineHeight: 1.6,
            maxWidth: '800px',
            margin: '0 auto'
          }}>
            <strong>AI, LLM, 최신 기술 기반의 맞춤형 솔루션 개발</strong>도 제공합니다.<br/>
            고객님의 비즈니스 요구사항에 맞는 <strong>전문적인 AI 솔루션</strong>을 개발해드립니다.
          </Typography>
        </Box>

        {/* 제공 서비스 */}
        <Typography variant="h6" sx={{ 
          fontWeight: 700, 
          color: '#1A365D', 
          mb: 3,
          textAlign: 'center',
          fontSize: { xs: '1rem', md: '1.2rem' }
        }}>
          🛠️ 제공하는 맞춤형 개발 서비스
        </Typography>

        <Grid container spacing={{ xs: 1.5, md: 2 }} sx={{ mb: { xs: 2, md: 4 } }}>
          {[
            {
              icon: <SmartToyIcon sx={{ fontSize: 24 }} />,
              title: 'AI Agent 개발',
              description: '업무 자동화를 위한 지능형 에이전트',
              keywords: ['자동화', '업무 효율성', '지능형 봇'],
              color: '#1A365D'
            },
            {
              icon: <PsychologyIcon sx={{ fontSize: 24 }} />,
              title: 'LLM 기반 애플리케이션',
              description: 'GPT, Claude 등 대형 언어모델 활용',
              keywords: ['ChatGPT', 'Claude', '커스텀 모델'],
              color: '#2D3748'
            },
            {
              icon: <SpeedIcon sx={{ fontSize: 24 }} />,
              title: 'RPA 솔루션',
              description: '반복 업무 자동화 및 워크플로우',
              keywords: ['업무 자동화', '프로세스', '효율성'],
              color: '#38A169'
            },
            {
              icon: <MemoryIcon sx={{ fontSize: 24 }} />,
              title: '지식베이스 시스템',
              description: '전문 지식 관리 및 검색 시스템',
              keywords: ['지식 관리', '검색 엔진', '정보 시스템'],
              color: '#D69E2E'
            },
            {
              icon: <ApiIcon sx={{ fontSize: 24 }} />,
              title: 'AI API 개발',
              description: '기존 시스템과 연동 가능한 AI 서비스',
              keywords: ['API', '시스템 연동', '마이크로서비스'],
              color: '#E53E3E'
            },
            {
              icon: <CodeIcon sx={{ fontSize: 24 }} />,
              title: '커스텀 AI 솔루션',
              description: '비즈니스 특화 맞춤형 AI 개발',
              keywords: ['맞춤 개발', '특화 솔루션', '비즈니스 로직'],
              color: '#718096'
            }
          ].map((service, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card sx={{
                height: '100%',
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(247, 250, 252, 0.95) 100%)',
                border: `2px solid ${service.color}15`,
                borderRadius: 3,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 12px 40px ${service.color}20`,
                  borderColor: `${service.color}30`,
                }
              }}>
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    mb: 2,
                    color: service.color
                  }}>
                    {service.icon}
                    <Typography variant="h6" sx={{ 
                      fontWeight: 700, 
                      ml: 1,
                      fontSize: { xs: '0.9rem', md: '1rem' },
                      color: service.color
                    }}>
                      {service.title}
                    </Typography>
                  </Box>
                  
                  <Typography variant="body2" sx={{ 
                    color: '#4A5568',
                    mb: 2,
                    fontSize: { xs: '0.8rem', md: '0.9rem' },
                    lineHeight: 1.4
                  }}>
                    {service.description}
                  </Typography>
                  
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    {service.keywords.map((keyword, idx) => (
                      <Chip
                        key={idx}
                        label={keyword}
                        size="small"
                        sx={{
                          fontSize: '0.7rem',
                          height: 20,
                          background: `${service.color}15`,
                          color: service.color,
                          border: `1px solid ${service.color}30`,
                          '& .MuiChip-label': {
                            px: 1
                          }
                        }}
                      />
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* 개발 프로세스 */}
        <Box sx={{ 
          background: 'linear-gradient(135deg, rgba(26, 54, 93, 0.08) 0%, rgba(214, 158, 46, 0.08) 100%)',
          borderRadius: 3,
          p: { xs: 2, md: 3 },
          mb: { xs: 2, md: 4 },
          border: '2px solid rgba(26, 54, 93, 0.1)'
        }}>
          <Typography variant="h6" sx={{ 
            fontWeight: 700, 
            color: '#1A365D', 
            mb: 3,
            textAlign: 'center',
            fontSize: { xs: '1rem', md: '1.2rem' }
          }}>
            📋 맞춤형 개발 프로세스
          </Typography>
          
          <Grid container spacing={2}>
            {[
              { step: '1', title: '요구사항 분석', desc: '비즈니스 요구사항 및 기술 스펙 정의' },
              { step: '2', title: '기술 설계', desc: 'AI 모델 선택 및 시스템 아키텍처 설계' },
              { step: '3', title: '개발 및 테스트', desc: '맞춤형 솔루션 개발 및 품질 검증' },
              { step: '4', title: '배포 및 운영', desc: '시스템 배포 및 지속적인 모니터링' }
            ].map((process, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Box sx={{ 
                  textAlign: 'center',
                  p: 2,
                  background: 'rgba(255, 255, 255, 0.7)',
                  borderRadius: 2,
                  border: '1px solid rgba(26, 54, 93, 0.1)',
                  height: '100%'
                }}>
                  <Box sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #1A365D 0%, #2D3748 100%)',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.2rem',
                    fontWeight: 'bold',
                    margin: '0 auto 12px',
                    boxShadow: '0 4px 12px rgba(26, 54, 93, 0.3)'
                  }}>
                    {process.step}
                  </Box>
                  <Typography variant="subtitle2" sx={{ 
                    fontWeight: 700, 
                    color: '#1A365D',
                    mb: 1,
                    fontSize: { xs: '0.8rem', md: '0.9rem' }
                  }}>
                    {process.title}
                  </Typography>
                  <Typography variant="caption" sx={{ 
                    color: '#4A5568',
                    fontSize: { xs: '0.7rem', md: '0.8rem' },
                    lineHeight: 1.3
                  }}>
                    {process.desc}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* CTA 섹션 */}
        <Box sx={{ 
          textAlign: 'center',
          p: { xs: 2, md: 3 },
          background: 'linear-gradient(135deg, #1A365D 0%, #2D3748 100%)',
          borderRadius: 3,
          color: 'white'
        }}>
          <Typography variant="h6" sx={{ 
            fontWeight: 700, 
            mb: 2,
            fontSize: { xs: '1rem', md: '1.2rem' },
            color: 'white',
            textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
          }}>
            💼 맞춤형 개발이 필요하신가요?
          </Typography>
          <Typography variant="body2" sx={{ 
            mb: 3,
            color: 'rgba(255, 255, 255, 0.95)',
            fontSize: { xs: '0.8rem', md: '0.95rem' },
            lineHeight: 1.5,
            fontWeight: 500
          }}>
            AI, LLM, RPA 등 최신 기술을 활용한 맞춤형 솔루션 개발을 원하시면<br/>
            <strong style={{ color: '#FBD38D' }}>도입 상담 요청</strong> 버튼을 클릭해주세요.
          </Typography>
          
          <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap">
            {['AI Agent', 'LLM 개발', 'RPA 솔루션', '커스텀 AI', 'API 개발'].map((tag, index) => (
              <Chip
                key={index}
                label={tag}
                sx={{
                  background: 'rgba(255, 255, 255, 0.25)',
                  color: 'white',
                  border: '1px solid rgba(255, 255, 255, 0.4)',
                  fontSize: '0.75rem',
                  height: 28,
                  fontWeight: 600,
                  '&:hover': {
                    background: 'rgba(255, 255, 255, 0.35)',
                    border: '1px solid rgba(255, 255, 255, 0.6)',
                  }
                }}
              />
            ))}
          </Stack>
        </Box>
      </DialogContent>

      <DialogActions sx={{ 
        p: { xs: 2, md: 3 }, 
        background: 'linear-gradient(135deg, rgba(247, 250, 252, 0.95) 0%, rgba(237, 242, 247, 0.95) 100%)',
        borderTop: '1px solid rgba(26, 54, 93, 0.1)',
        flexDirection: { xs: 'row', sm: 'row' },
        justifyContent: 'space-between',
        gap: 1
      }}>
        <Button 
          onClick={onClose}
          sx={{ 
            color: '#4A5568',
            fontWeight: 600,
            fontSize: { xs: '0.8rem', md: '0.9rem' },
            px: { xs: 2, md: 3 },
            py: { xs: 1, md: 1.5 },
            minWidth: 'auto',
            flex: { xs: 1, sm: 'none' },
            '&:hover': { background: 'rgba(74, 85, 104, 0.1)' }
          }}
        >
          나중에 보기
        </Button>
        <Button 
          variant="contained"
          onClick={handleContactClick}
          startIcon={<BusinessCenterIcon sx={{ fontSize: { xs: 16, md: 20 } }} />}
          sx={{ 
            background: 'linear-gradient(135deg, #1A365D 0%, #2D3748 100%)',
            boxShadow: '0 8px 24px rgba(26, 54, 93, 0.3)',
            fontWeight: 'bold',
            fontSize: { xs: '0.8rem', md: '0.9rem' },
            px: { xs: 2, md: 4 },
            py: { xs: 1, md: 1.5 },
            minWidth: 'auto',
            flex: { xs: 1, sm: 'none' },
            '&:hover': {
              background: 'linear-gradient(135deg, #153E75 0%, #1A365D 100%)',
              transform: 'translateY(-2px)',
              boxShadow: '0 12px 32px rgba(26, 54, 93, 0.4)',
            },
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
          }}
        >
          도입 상담 요청
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PromotionModal;
